import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { App, Button, Input } from 'antd';

import { api, readTotalCount } from '../api/client';
import { API_COLLECTIONS_URL } from '../utils';
import { canRead, isGlobalConsumer, isOwner } from '../utils/permissions';
import { normalizeId } from '../utils/ids';
import { isPersonal } from '../utils/collectionKinds';
import { orderWithSubCollections, subCollectionCountsIn } from '../utils/collectionFamily';
import { orderByFavorites } from '../utils/collections';
import { AdminJSON } from '../utils/admin';
import { COMPASS, HISTORY, MAGNIFYING_GLASS, PLUS } from '../components/icons';
import CollectionList, { CollectionStrip } from '../components/collections/CollectionList';
import ContextBar from '../components/common/ContextBar';
import GroupHeader from '../components/common/GroupHeader';
import { ReorderButton } from '../components/common/SortableList';
import FilterBuilder from '../components/common/FilterBuilder';
import FilterChips from '../components/common/FilterChips';
import useCollectionCounts from '../hooks/useCollectionCounts';
import useListQuery from '../hooks/useListQuery';
import { useCardDefinitions } from '../contexts/CardDefinitionsContext';
import { useFavorites } from '../contexts/FavoritesContext';
import { buildCollectionFilterFields } from '../utils/listFilterFields';
import { parseForBuilder, readSearchTerm, withSearchTerm } from '../utils/kqlBuilder';
import { serialize } from '../utils/kql';
import { NO_TEXT_ASSIST } from '../utils/fieldAssist';
import { DataState, Skeleton, deriveDataState } from '../components/loading';

const SEARCH_DEBOUNCE_MS = 250;

// A section is the platform group header (micro-label · rule · count as
// words) over its grid. Favorites render as a compact strip — chip + name +
// counts — so six favorites never orphan a two-tile row above the full list.
//
// The Favorites strip is the person's ONE favorites order (FavoritesContext —
// the rail's Pinned and Home's Collections draw it too), and it carries the
// same Reorder button Home's sections do.
const CollectionSection = ({ title, collections, showPlaceholder, countsMap, compact = false, onUnfollowed, family, reorder = null }) => (
	<section className="collection-section">
		<GroupHeader
			as="h2"
			label={title}
			count={collections.length}
			countLabel="collection"
			action={reorder ? <ReorderButton active={reorder.active} onClick={reorder.onToggle} what="favorite collections" /> : null}
		/>
		{compact ? (
			<CollectionStrip
				data={collections}
				countsMap={countsMap}
				reordering={Boolean(reorder?.active)}
				onReorder={reorder?.onReorder}
			/>
		) : (
			<CollectionList
				data={collections}
				showPlaceholder={showPlaceholder}
				countsMap={countsMap}
				onUnfollowed={onUnfollowed}
				family={family}
			/>
		)}
	</section>
);

export default function Collections() {
	const navigate = useNavigate();
	const { message } = App.useApp();
	// The list is filtered the way every list on the platform is: a KQL
	// query in ?q=, compiled server-side (the collections subject — see
	// docs/plans/list-queries.md).
	const { query, setQuery } = useListQuery();
	const [collections, setCollections] = useState([]);
	const [totalCount, setTotalCount] = useState(null);
	const [loading, setLoading] = useState(true);
	const [loadError, setLoadError] = useState(null);
	// Retry bumps this so the list is read again for the same filter.
	const [reloadKey, setReloadKey] = useState(0);
	const countsMap = useCollectionCounts(collections);

	// A query the server refuses (a stale link, an API without the query
	// engine) is dropped rather than left to fail on every render — the
	// items page idiom.
	const handleQueryError = useCallback(serverMessage => {
		message.error(serverMessage || 'That filter could not be read, so it was removed.');
		setQuery(undefined);
	}, [message, setQuery]);
	// Read by the fetch below at the moment of a refusal, never as its
	// dependency: the list is fetched again when `query` changes, and for
	// nothing else in the address.
	const handleQueryErrorRef = useRef(handleQueryError);
	handleQueryErrorRef.current = handleQueryError;

	// Generation guard: a response from before a filter change must never
	// land on the new list. The cleanup bumps it too, so an unmount (or a
	// StrictMode re-run) discards the in-flight response the same way.
	const generationRef = useRef(0);
	useEffect(() => {
		const generation = ++generationRef.current;
		setLoading(true);
		setLoadError(null);
		const params = {};
		// Blank is simply no filter; the server ANDs q with the caller's scope.
		if (query) params.q = query;
		api.get(API_COLLECTIONS_URL, { params })
			.then(({ data, headers }) => {
				if (generation !== generationRef.current) return;
				const list = data || [];
				setCollections(list);
				setTotalCount(readTotalCount(headers, list.length));
				setLoadError(null);
			})
			.catch(error => {
				if (generation !== generationRef.current) return;
				// The only 400 a filtered list gets is a malformed q.
				if (query && error?.response?.status === 400) {
					handleQueryErrorRef.current(error.response?.data?.message);
					return;
				}
				setLoadError(error);
			})
			.finally(() => {
				if (generation === generationRef.current) setLoading(false);
			});
		return () => { generationRef.current += 1; };
	}, [query, reloadKey]);

	// ---- Vocabulary: the collections subject, with the card picker fed by
	//      the app-wide card cache ----
	const { cards } = useCardDefinitions();
	const filterFields = useMemo(() => buildCollectionFilterFields({ cards, collections }), [cards, collections]);

	// ---- The search box: sugar over the one `text ~ …` condition. The URL
	//      is the source of truth; the box is a debounced scratch copy of it.
	//      Chips hide that condition — the box already shows it. ----
	const { ast } = useMemo(() => parseForBuilder(query), [query]);
	const searchTerm = useMemo(() => readSearchTerm(ast, 'text'), [ast]);
	const [searchText, setSearchText] = useState(searchTerm);
	const searchTimerRef = useRef(null);
	const searchRef = useRef(null);
	// The latest query for the debounced commit, which fires after this
	// render's closure has gone stale.
	const queryRef = useRef(query);
	queryRef.current = query;

	useEffect(() => {
		// URL → box (a chip dismissal, back/forward), unless the user is
		// mid-typing and the box is about to win anyway.
		if (!searchTimerRef.current) setSearchText(searchTerm);
	}, [searchTerm]);
	useEffect(() => () => clearTimeout(searchTimerRef.current), []);

	const applySearch = useCallback(term => {
		const { ast: current } = parseForBuilder(queryRef.current);
		const next = withSearchTerm(current, term, 'text');
		setQuery(next ? serialize(next) : undefined);
	}, [setQuery]);

	const commitSearchNow = useCallback(term => {
		clearTimeout(searchTimerRef.current);
		searchTimerRef.current = null;
		applySearch(term);
	}, [applySearch]);

	const handleSearchChange = event => {
		const value = event.target.value;
		setSearchText(value);
		// Clearing (the ✕, or backspacing to nothing) should not wait.
		if (!value.trim()) {
			commitSearchNow('');
			return;
		}
		clearTimeout(searchTimerRef.current);
		searchTimerRef.current = setTimeout(() => {
			searchTimerRef.current = null;
			applySearch(value);
		}, SEARCH_DEBOUNCE_MS);
	};

	// `/` focuses the search unless focus already sits in an input, a select,
	// or an open modal/popover — the platform keyboard map's filter key.
	useEffect(() => {
		const handler = event => {
			if (event.key !== '/' || event.metaKey || event.ctrlKey || event.altKey) return;
			const target = event.target;
			if (target instanceof Element) {
				if (target.closest('input, textarea, select, [contenteditable="true"], .ant-modal, .ant-popover, .ant-drawer, .ant-select')) return;
			}
			event.preventDefault();
			searchRef.current?.focus?.();
		};
		document.addEventListener('keydown', handler);
		return () => document.removeEventListener('keydown', handler);
	}, []);

	// Favorites are per-user (FavoritesContext); a toggle anywhere re-renders
	// every section, Favorites included, through the context. Their ORDER is
	// the person's own — the rail's and Home's — and is reordered here too.
	const { isFavoriteCollection, favoriteCollectionOrder, reorderCollections } = useFavorites();
	const [reorderingFavorites, setReorderingFavorites] = useState(false);

	// Sections derive from the server-computed myAccess role, not from raw
	// ownedBy/sharedWith fields: "Shared with me" is EVERYTHING readable that
	// isn't owned (grants, group grants, and card-scoped access included), so
	// no reachable collection can fall outside every section. A query has
	// already filtered the list server-side, so the sections split what
	// matched.
	const favCollections = orderByFavorites(collections.filter(c => isFavoriteCollection(c?._id)), favoriteCollectionOrder);
	// The personal collection is the first tile under My collections. The API
	// already lists it first; sorting here too keeps that true against an API
	// a deploy behind, and the sort is stable for everything else.
	const myCollections = orderWithSubCollections(collections
		.filter(c => isOwner(c))
		.sort((a, b) => Number(isPersonal(b)) - Number(isPersonal(a))));
	// GLOBAL: the global collections the caller follows and reads only as a
	// consumer (plan §4.7) — between Favorites and My collections. One they
	// also hold a grant on is theirs to work in, so it stays under Shared.
	const globalCollections = collections.filter(c => isGlobalConsumer(c));
	const sharedCollections = orderWithSubCollections(collections.filter(c => !isOwner(c) && canRead(c) && !isGlobalConsumer(c)));
	const onUnfollowed = useCallback(id => {
		setCollections(prev => prev.filter(c => normalizeId(c?._id) !== normalizeId(id)));
	}, []);

	// SUB-COLLECTIONS (docs/plans/sub-collections.md §5.1): an ordinary tile,
	// sorted immediately after its parent in whichever section the parent is
	// in — no section of its own, no indent, no tree. One whose parent the
	// viewer cannot see keeps its place (in Shared with me). The tiles' meta
	// line reads the family off the whole list.
	const family = useMemo(() => ({
		subCounts: subCollectionCountsIn(collections),
		visibleIds: new Set(collections.map(c => normalizeId(c?._id)).filter(Boolean)),
	}), [collections]);

	const sections = [
		{ key: 'favorites', title: 'Favorites', collections: favCollections, compact: true },
		// Only while there is one: an empty "Global" would be a heading about
		// a feature, not a fact about the person's collections.
		{ key: 'global', title: 'Global', collections: globalCollections, optional: true },
		{ key: 'mine', title: 'My collections', collections: myCollections },
		{ key: 'shared', title: 'Shared with me', collections: sharedCollections },
	];
	const isFiltered = Boolean(query);
	// Reorder only the whole list: a filtered strip is a subset, and moving
	// one of its rows would silently jump it past the favorites the filter
	// hides — so a filter ends the mode, and the button waits for the full
	// list. In the mode the button stays (Done) whatever an unstar leaves.
	useEffect(() => {
		if (isFiltered) setReorderingFavorites(false);
	}, [isFiltered]);
	const favoritesReorder = typeof reorderCollections === 'function' && !isFiltered
		&& (favCollections.length >= 2 || reorderingFavorites)
		? {
			active: reorderingFavorites,
			onToggle: () => setReorderingFavorites(active => !active),
			onReorder: reorderCollections,
		}
		: null;
	// Unfiltered, every section shows (an empty "Shared with me" is a fact
	// worth stating). Filtered, an empty section is just noise — and when
	// nothing matched at all, the page says so once.
	const hideEmpty = isFiltered && !loading;
	const visibleSections = sections.filter(section => (
		section.collections.length || (!hideEmpty && !section.compact && !section.optional)
	));
	const noMatches = hideEmpty && visibleSections.length === 0;

	return (
		<>
			{/* The context bar: title, the one filled primary at the right
			    edge, and the list route's filter row as its second row —
			    search · applied-filter chips · + Filter · spacer · count. */}
			<ContextBar
				title="Collections"
				actions={(
					<>
						{/* A labeled secondary: the phone's door to Discover (no
						    rail, no sixth tab — ruling e09), and a sensible one on a
						    wide screen too, beside making a collection by hand. */}
						<Button icon={<COMPASS />} onClick={() => navigate('/discover')}>
							Discover
						</Button>
						<Button
							type="primary"
							icon={<PLUS />}
							onClick={() => navigate('/collections/new')}
						>
							New collection
						</Button>
					</>
				)}
				// The door to the collections you deleted (delete audit F5):
				// a deleted collection cannot be opened, so its undo is here.
				menuActions={[{
					key: 'recently-deleted',
					group: 'navigate',
					icon: <HISTORY />,
					label: 'Recently deleted',
					onClick: () => navigate('/collections/deleted'),
				}]}
			>
			<div className="list-toolbar list-toolbar--in-bar">
				<Input
					{...NO_TEXT_ASSIST}
					ref={searchRef}
					className="list-toolbar__search"
					allowClear
					prefix={<MAGNIFYING_GLASS />}
					placeholder="Search collections…"
					aria-label="Search collections"
					value={searchText}
					onChange={handleSearchChange}
					onPressEnter={() => commitSearchNow(searchText)}
				/>

				{query ? (
					<FilterChips
						query={query}
						fields={filterFields}
						onChange={setQuery}
						hiddenPaths={['text']}
					/>
				) : null}

				<FilterBuilder
					fields={filterFields}
					query={query}
					onApply={setQuery}
					triggerId="collections-filter-trigger"
					emptyText="No conditions yet — every collection you can see is shown."
					placeholder="card = person and status = active"
				/>

				<span className="list-toolbar__spacer" />

				{typeof totalCount === 'number' ? (
					<span className="list-toolbar__count">
						{totalCount === 1 ? '1 collection' : `${totalCount} collections`}
					</span>
				) : null}
			</div>
			</ContextBar>

			{/* The rows stay on screen through a filter change or a re-read
			    (refreshing); a failed one is a notice above them with Retry. */}
			<DataState
				state={deriveDataState({ loading, error: loadError, hasData: totalCount !== null })}
				hasData={totalCount !== null}
				skeleton={<Skeleton variant="tile" count={6} />}
				error={loadError ? 'Check your connection and try again.' : null}
				onRetry={() => setReloadKey(n => n + 1)}
				label="collections"
			>
				{noMatches ? (
					<div className="list-empty">
						<span>No collections match</span>
						<Button onClick={() => setQuery(undefined)}>Clear filters</Button>
					</div>
				) : (
					visibleSections.map(section => (
						<CollectionSection
							key={section.key}
							title={section.title}
							collections={section.collections}
							showPlaceholder={false}
							countsMap={countsMap}
							compact={section.compact}
							onUnfollowed={onUnfollowed}
							family={family}
							reorder={section.key === 'favorites' ? favoritesReorder : null}
						/>
					))
				)}
			</DataState>

			<AdminJSON obj={collections} label="collections" />
		</>
	);
}
